import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useIconUrl from '../../utils/hooks/useIconUrl';
import useLocalize from '../../utils/hooks/useLocalize';
import { selectCandidates } from '../../utils/scanCandidates';
import { renderCountChunk } from './aiScannerChunks';
import StockCard from './StockCard';

// STORY-013: the results step, appended to the conversation once both
// investment questions are answered — confirmation, count, AI summary, candidate
// cards, favorites button and disclaimer. Each card's logo is the Wharf icon
// built here from the current token (STORY-011).
export const ScanResults = ({ symbols = [] }) => {
  const localize = useLocalize();
  const iconUrl = useIconUrl();
  const [selected, setSelected] = useState(() => new Set());

  const summaryText = useSelector((state) => state.scans.aiSummary.text);
  const symbolMetadata = useSelector((state) => state.symbols.data);

  const candidates = useMemo(() => selectCandidates(symbols), [symbols]);

  // The button reads the selection intersected with the on-screen cards, so a
  // tick left over from a no-longer-rendered card does not keep it enabled.
  const activeSelection = useMemo(
    () => candidates.filter((symbolId) => selected.has(symbolId)),
    [candidates, selected]
  );

  const handleToggle = useCallback((symbolId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(symbolId)) next.add(symbolId);
      return next;
    });
  }, []);

  const description = localize('ai.scanner.results.description.placeholder');

  return (
    <div className="ai-scanner-results">
      <div className="ai-scanner-message">
        <p className="ai-scanner-results-confirmation">
          {localize('ai.scanner.results.confirmation')}
        </p>
        <p>
          {localize('ai.scanner.results.count', {
            count: String(candidates.length),
            b: renderCountChunk
          })}
        </p>
        {/* Bedrock is not enabled in QA yet, so an absent summary is normal. */}
        <p className="ai-scanner-results-summary">
          {summaryText || localize('ai.scanner.results.summary.placeholder')}
        </p>
        <p>{localize('ai.scanner.results.helper')}</p>
      </div>
      <ul className="ai-scanner-results-cards">
        {candidates.map((symbolId) => {
          // `symbols.data[id]` is the request envelope; metadata is under `.data`.
          const meta = (symbolMetadata[symbolId] || {}).data || {};
          return (
            <StockCard
              key={symbolId}
              symbolId={symbolId}
              name={meta.jpShortName || meta.name || symbolId}
              description={description}
              logoUrl={iconUrl(symbolId)}
              selected={selected.has(symbolId)}
              onToggle={handleToggle}
            />
          );
        })}
      </ul>
      {/* Display-only until the board-selection modal lands (STORY-014). */}
      <button
        type="button"
        className="ai-scanner-results-register"
        disabled={activeSelection.length === 0}
      >
        {localize('ai.scanner.results.register')}
      </button>
      <p className="ai-scanner-results-disclaimer">
        {localize('ai.scanner.results.disclaimer')}
      </p>
    </div>
  );
};

ScanResults.propTypes = {
  symbols: PropTypes.arrayOf(PropTypes.string)
};

export default ScanResults;
